import {
  useState, useEffect, useRef, useCallback,
} from 'react';
import { Platform } from 'react-native';
import log from '../../../common/utils/logger';

const STORAGE_KEY = 'flare_student_last_task_id';

// Module-level variable to track if we've already performed the "initial" check
// (which handles the reload/refresh case) for this session/tab life.
let isInitialCheckPerformed = false;

export interface TaskSafetyFuseResult {
  conflict: string | null; // The ID of the previous task if conflict exists
  resolveConflict: (choice: 'stay_current' | 'restore_previous') => void;
  isChecked: boolean;
}

/**
 * A safety fuse to detect if the user refreshed the window but the server context
 * has switched to a different task (e.g. from another window).
 *
 * If a conflict is detected (Reload + ID Mismatch), it returns the previousTaskId.
 * This allows the UI to block autosave and ask the user for resolution.
 */
export const useTaskSafetyFuse = (currentTaskId: string | undefined): TaskSafetyFuseResult => {
  const [conflict, setConflict] = useState<string | null>(null);
  const [isChecked, setIsChecked] = useState(false);
  const hasCheckedRef = useRef(false);

  // Initial check on mount/load
  useEffect(() => {
    if (Platform.OS !== 'web') {
      setIsChecked(true);
      return;
    }

    // Only run the initial check once per page load.
    // If we've already checked since the last refresh, or if we don't have an ID yet, skip the reload check.
    if (!currentTaskId || isInitialCheckPerformed || hasCheckedRef.current) {
      if (currentTaskId && !hasCheckedRef.current) {
        // This is the first time we see a valid ID in this component instance,
        // but not a reload-based conflict. Mark as checked and save the ID.
        sessionStorage.setItem(STORAGE_KEY, currentTaskId);
        hasCheckedRef.current = true;
        setIsChecked(true);
      }
      return;
    }

    try {
      // Check if this is a reload
      const navEntry = window.performance?.getEntriesByType?.('navigation')?.[0] as PerformanceNavigationTiming;
      const isReload = navEntry?.type === 'reload' || navEntry?.type === 'back_forward';

      // IMPORTANT: Only treat as a reload conflict if the TaskScreen is the FIRST screen
      // shown after the reload. If the app has been running for a bit, it's a client navigation.
      const isInitialAppMount = window.performance.now() < 3000;

      const lastId = sessionStorage.getItem(STORAGE_KEY);

      if (isReload && isInitialAppMount && lastId && lastId !== currentTaskId) {
        // Conflict! The browser was reloaded, but the task ID changed.
        log.warn(`[TaskSafetyFuse] Conflict detected. Previous: ${lastId}, Current: ${currentTaskId}`);
        setConflict(lastId);
      } else {
        // No conflict, or standard navigation. Sync storage.
        sessionStorage.setItem(STORAGE_KEY, currentTaskId);
      }
    } catch (e) {
      log.error('[TaskSafetyFuse] Error checking safety fuse', e);
    } finally {
      isInitialCheckPerformed = true;
      hasCheckedRef.current = true;
      setIsChecked(true);
    }
  }, [currentTaskId]);

  // Sync storage on subsequent valid navigations
  useEffect(() => {
    if (Platform.OS !== 'web' || !currentTaskId) return;

    // Only update storage if we have already passed the initial check AND there is no active conflict
    if (hasCheckedRef.current && !conflict) {
      sessionStorage.setItem(STORAGE_KEY, currentTaskId);
    }
  }, [currentTaskId, conflict]);

  const resolveConflict = useCallback(
    (choice: 'stay_current' | 'restore_previous') => {
      if (choice === 'stay_current' && currentTaskId) {
        // User accepts the new task
        sessionStorage.setItem(STORAGE_KEY, currentTaskId);
        setConflict(null);
      } else if (choice === 'restore_previous') {
        // User wants to go back.
        // We clear the conflict so the UI unlocks, but the caller handles the navigation/state change
        setConflict(null);
      }
    },
    [currentTaskId],
  );

  return {
    conflict,
    resolveConflict,
    isChecked,
  };
};
